import type { DomainDetailDto } from "@bafe/api-client";
import type { CheckState } from "@bafe/types";
import { CLIENT_ENDPOINTS } from "@bafe/config";
import { Button, Card, Checkbox, CopyField, EmptyState, InlineNotice, PageHeader, Section, StatusPill } from "@bafe/ui";
import { formatBytes } from "@bafe/validation";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router";

import { DnsRecordList } from "../../components/DnsRecordList";
import { LinkButton } from "../../components/LinkButton";
import { MailboxTable } from "../../components/MailboxTable";
import { QueryState } from "../../components/QueryState";
import { TabNav } from "../../components/TabNav";
import { api } from "../../lib/api";
import { formatCount, formatRelative } from "../../lib/format";
import { queryKeys } from "../../lib/query";
import { CHECK_STATE, DOMAIN_STATUS } from "../../lib/status";

const TABS = ["overview", "dns", "security", "mailboxes", "advanced"] as const;
type TabId = (typeof TABS)[number];

const TAB_LABEL: Record<TabId, string> = {
	overview: "Overview",
	dns: "DNS",
	security: "Security",
	mailboxes: "Mailboxes",
	advanced: "Advanced",
};

function visibleTabs(detail: DomainDetailDto): TabId[] {
	return TABS.filter((id) => {
		if (id === "mailboxes" || id === "security") return detail.emailEnabled;
		return true;
	});
}

function isTabId(value: string | null): value is TabId {
	return TABS.includes((value ?? "") as TabId);
}

export function DomainDetail() {
	const { id = "" } = useParams();
	const [searchParams] = useSearchParams();
	const rawTab = searchParams.get("tab");
	const tab: TabId = isTabId(rawTab) ? rawTab : "overview";

	const domain = useQuery({
		queryKey: queryKeys.domain(id),
		queryFn: () => api.getDomain(id),
		enabled: id !== "",
	});

	return (
		<div className="flex flex-col gap-6">
			<QueryState query={domain} errorTitle="We could not load this domain.">
				{(detail) => (
					<>
						<PageHeader
							title={detail.domainName}
							description={DOMAIN_STATUS[detail.status].label}
							breadcrumb={
								<Link
									to="/manage/domains"
									className="text-sm text-(--text-secondary) underline underline-offset-2 hover:text-(--text-primary)"
								>
									Domains
								</Link>
							}
							action={<RecheckButton domainId={detail.id} />}
						/>

						<TabNav
							label="Domain sections"
							currentId={visibleTabs(detail).includes(tab) ? tab : "overview"}
							items={visibleTabs(detail).map((id) => ({
								id,
								label: TAB_LABEL[id],
								to: `/manage/domains/${detail.id}?tab=${id}`,
							}))}
						/>

						{tab === "overview" && <OverviewTab detail={detail} />}
						{tab === "dns" && <DnsTab detail={detail} />}
						{tab === "security" && <SecurityTab detail={detail} />}
						{tab === "mailboxes" && <MailboxesTab detail={detail} />}
						{tab === "advanced" && <AdvancedTab detail={detail} />}
					</>
				)}
			</QueryState>
		</div>
	);
}

function RecheckButton({ domainId }: { domainId: string }) {
	const queryClient = useQueryClient();

	const recheck = useMutation({
		mutationFn: () => api.checkDomainDns(domainId),
		onSuccess: (detail) => {
			queryClient.setQueryData(queryKeys.domain(detail.id), detail);
			void queryClient.invalidateQueries({ queryKey: queryKeys.domains });
		},
	});

	return (
		<Button
			variant="secondary"
			pending={recheck.isPending}
			pendingLabel="Checking…"
			onClick={() => recheck.mutate()}
		>
			Check DNS now
		</Button>
	);
}

function OverviewTab({ detail }: { detail: DomainDetailDto }) {
	const attention = Object.entries(detail.checks).filter(
		([, state]) => state === "warn" || state === "fail",
	).length;

	return (
		<div className="flex flex-col gap-6">
			{detail.status === "pending" ||
			(detail.emailEnabled && detail.mailReadyAt === null) ||
			(detail.sitesEnabled && detail.sitesReadyAt === null) ||
			(detail.platformApp && detail.platformReadyAt === null) ? (
				<InlineNotice
					tone="caution"
					title="Setup is not finished"
					action={
						<LinkButton to={`/manage/domains/new?domain=${detail.id}`} size="sm" variant="secondary">
							Finish setup
						</LinkButton>
					}
				>
					{detail.emailEnabled
						? "This domain cannot send or receive mail until the remaining DNS records are in place."
						: "Add the remaining DNS records so this hostname can finish connecting."}
				</InlineNotice>
			) : null}

			<div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
				<Fact
					label="Purpose"
					value={[
						detail.emailEnabled ? "Email" : null,
						detail.sitesEnabled ? "Website" : null,
						detail.platformApp === "hub" ? "Hub" : null,
						detail.platformApp === "webmail" ? "Webmail" : null,
					]
						.filter(Boolean)
						.join(" · ")}
				/>
				<Fact label="Status" value={DOMAIN_STATUS[detail.status].label} />
				<Fact
					label="Email accounts"
					value={String(detail.mailboxCount)}
					footer={
						<Link className="underline underline-offset-2" to={`/manage/domains/${detail.id}?tab=mailboxes`}>
							View accounts
						</Link>
					}
				/>
				<Fact label="Storage used" value={formatBytes(detail.usedBytes)} />
				<Fact
					label="Email security"
					value={attention === 0 ? "All checks passing" : formatCount(attention, "check needs attention", "checks need attention")}
					footer={
						<Link className="underline underline-offset-2" to={`/manage/domains/${detail.id}?tab=security`}>
							View security
						</Link>
					}
				/>
			</div>

			<Section title="Aliases and DNS">
				<Card className="flex flex-col gap-3">
					<Row label="Aliases" value={formatCount(detail.aliasCount, "alias", "aliases")} />
					<Row label="DNS last checked" value={formatRelative(detail.lastDnsCheckAt)} />
					<Row
						label="Ownership verified"
						value={detail.ownershipVerifiedAt ? formatRelative(detail.ownershipVerifiedAt) : "Not yet"}
					/>
					{detail.emailEnabled && (
						<Row
							label="Ready for mail"
							value={detail.mailReadyAt ? formatRelative(detail.mailReadyAt) : "Not yet"}
						/>
					)}
					{detail.sitesEnabled && (
						<Row
							label="Ready for website"
							value={detail.sitesReadyAt ? formatRelative(detail.sitesReadyAt) : "Not yet"}
						/>
					)}
					{detail.platformApp && (
						<Row
							label={detail.platformApp === "hub" ? "Ready for Hub" : "Ready for Webmail"}
							value={detail.platformReadyAt ? formatRelative(detail.platformReadyAt) : "Not yet"}
						/>
					)}
				</Card>
			</Section>

			{!detail.platformApp && (
				<CapabilitiesPanel detail={detail} />
			)}

			<Section
				title="Remove this domain"
				description="Removing a domain stops its mail and schedules its data for deletion."
			>
				<div>
					<LinkButton to={`/manage/domains/${detail.id}/remove`} variant="secondary">
						Remove domain
					</LinkButton>
				</div>
			</Section>
		</div>
	);
}

function DnsTab({ detail }: { detail: DomainDetailDto }) {
	return (
		<div className="flex flex-col gap-6">
			{detail.conflicts.length > 0 && (
				<InlineNotice tone="critical" title="Conflicting records found">
					<div className="flex flex-col gap-1">
						{detail.conflicts.map((conflict) => (
							<p key={conflict.message}>{conflict.message}</p>
						))}
					</div>
				</InlineNotice>
			)}

			<Section
				title="DNS records"
				description="Add these at whoever manages your domain's DNS. Key checks them for you."
			>
				<DnsRecordList records={detail.records} />
			</Section>
		</div>
	);
}

interface SecurityItem {
	id: string;
	label: string;
	state: CheckState | null;
	sentence: string;
}

function SecurityTab({ detail }: { detail: DomainDetailDto }) {
	const items: SecurityItem[] = [
		{
			id: "spf",
			label: "SPF",
			state: detail.checks.spf,
			sentence: "Tells other mail servers that Key is allowed to send email for your domain.",
		},
		{
			id: "dkim",
			label: "DKIM",
			state: detail.checks.dkim,
			sentence: "Signs every message you send so the receiver can tell it was not altered on the way.",
		},
		{
			id: "dmarc",
			label: "DMARC",
			state: detail.checks.dmarc,
			sentence: "Tells receivers what to do with mail that claims to be from you but fails the checks above.",
		},
		{
			id: "tls",
			label: "Encryption in transit",
			// Managed by Key, so there is nothing for the customer to check.
			state: null,
			sentence: "Mail to and from Key is encrypted whenever the other server supports it. Key manages this.",
		},
	];

	return (
		<Section
			title="Email security"
			description="These three records are what stop other people sending email that looks like it came from you."
		>
			<Card flush>
				<ul>
					{items.map((item) => (
						<li
							key={item.id}
							className="flex flex-wrap items-center justify-between gap-3 border-b border-(--border-subtle) px-4 py-3.5 last:border-0"
						>
							<div className="flex min-w-0 max-w-prose flex-col gap-0.5">
								<p className="text-sm font-medium text-(--text-primary)">{item.label}</p>
								<p className="text-sm text-(--text-secondary)">{item.sentence}</p>
							</div>
							{item.state === null ? (
								<StatusPill tone="info">Managed by Key</StatusPill>
							) : (
								<StatusPill tone={CHECK_STATE[item.state].tone}>
									{CHECK_STATE[item.state].label}
								</StatusPill>
							)}
						</li>
					))}
				</ul>
			</Card>

			<p className="max-w-prose text-sm text-(--text-secondary)">
				If something here needs attention, the DNS tab shows the exact record to add.
			</p>
		</Section>
	);
}

function MailboxesTab({ detail }: { detail: DomainDetailDto }) {
	const mailboxes = useQuery({
		queryKey: queryKeys.mailboxes(detail.id),
		queryFn: () => api.listMailboxes({ domainId: detail.id }),
		refetchOnMount: "always",
	});

	return (
		<Section
			title={`Email accounts on ${detail.domainName}`}
			action={
				<LinkButton to={`/mail/mailboxes/new?domain=${detail.id}`} variant="primary" size="sm">
					Create mailbox
				</LinkButton>
			}
		>
			<QueryState query={mailboxes} errorTitle="We could not load the email accounts for this domain.">
				{(page) => (
					<Card flush>
						<MailboxTable
							caption={`Email accounts on ${detail.domainName}`}
							rows={page.data}
							hideDomain
							empty={
								<EmptyState
									title="No email accounts on this domain yet"
									description="Create one and it can start sending and receiving straight away."
									action={
										<LinkButton to={`/mail/mailboxes/new?domain=${detail.id}`} variant="primary">
											Create mailbox
										</LinkButton>
									}
								/>
							}
						/>
					</Card>
				)}
			</QueryState>
		</Section>
	);
}

function AdvancedTab({ detail }: { detail: DomainDetailDto }) {
	const mxRecords = detail.records.filter((record) => record.purpose === "mx");
	const observations = detail.records.filter((record) => record.observedValues.length > 0);

	return (
		<div className="flex flex-col gap-6">
			<Section
				title="Technical details"
				description="You do not need any of this for day-to-day use. It is here for troubleshooting."
			>
				<Card className="flex flex-col gap-4">
					<div className="flex flex-col gap-2">
						<p className="text-xs font-medium text-(--text-muted)">MX records</p>
						{mxRecords.length === 0 ? (
							<p className="text-sm text-(--text-secondary)">No MX records issued yet.</p>
						) : (
							<ul className="flex flex-col gap-2">
								{mxRecords.map((record) => (
									<li key={record.id}>
										<CopyField
											value={`${record.priority ?? ""} ${record.value}`.trim()}
											label="MX record"
										/>
									</li>
								))}
							</ul>
						)}
					</div>

					<LabelledCopy label="Mail host" value={CLIENT_ENDPOINTS.imap.host} />
					<LabelledCopy label="DKIM selector" value={detail.dkimSelector} />
					<LabelledCopy label="Domain ID" value={detail.id} />
					<LabelledCopy label="Provisioning state" value={detail.provisioningState} />
				</Card>
			</Section>

			<Section title="Client endpoints">
				<Card className="flex flex-col gap-4">
					<LabelledCopy
						label="IMAP"
						value={`${CLIENT_ENDPOINTS.imap.host}:${CLIENT_ENDPOINTS.imap.port} (${CLIENT_ENDPOINTS.imap.security})`}
					/>
					<LabelledCopy
						label="SMTP"
						value={`${CLIENT_ENDPOINTS.submissionImplicit.host}:${CLIENT_ENDPOINTS.submissionImplicit.port} (${CLIENT_ENDPOINTS.submissionImplicit.security})`}
					/>
					<LabelledCopy
						label="SMTP (STARTTLS)"
						value={`${CLIENT_ENDPOINTS.submissionStartTls.host}:${CLIENT_ENDPOINTS.submissionStartTls.port} (${CLIENT_ENDPOINTS.submissionStartTls.security})`}
					/>
					<LabelledCopy label="Automatic discovery" value={CLIENT_ENDPOINTS.jmap.url} />
				</Card>
			</Section>

			<Section title="What we see in DNS right now">
				{observations.length === 0 ? (
					<Card>
						<p className="text-sm text-(--text-secondary)">
							Nothing recorded from the last lookup. Run a check from the top of this page.
						</p>
					</Card>
				) : (
					<Card className="flex flex-col gap-4">
						{observations.map((record) => (
							<div key={record.id} className="flex flex-col gap-1.5">
								<p className="text-xs font-medium text-(--text-muted)">
									{record.recordType} {record.host}
								</p>
								<ul className="flex flex-col gap-1">
									{record.observedValues.map((value) => (
										<li key={value} className="text-dns text-(--text-secondary)">
											{value}
										</li>
									))}
								</ul>
							</div>
						))}
					</Card>
				)}
			</Section>
		</div>
	);
}

function CapabilitiesPanel({ detail }: { detail: DomainDetailDto }) {
	const queryClient = useQueryClient();
	const [email, setEmail] = useState(detail.emailEnabled);
	const [sites, setSites] = useState(detail.sitesEnabled);
	const [includeWww, setIncludeWww] = useState(detail.includeWww);

	const save = useMutation({
		mutationFn: () => api.updateDomainCapabilities(detail.id, { email, sites, includeWww }),
		onSuccess: (updated) => {
			queryClient.setQueryData(queryKeys.domain(updated.id), updated);
			void queryClient.invalidateQueries({ queryKey: queryKeys.domains });
		},
	});

	const dirty = email !== detail.emailEnabled || sites !== detail.sitesEnabled || includeWww !== detail.includeWww;

	return (
		<Section
			title="Purposes"
			description="Email and a website can share this hostname. Hub and Webmail stay on their own names."
		>
			<Card className="max-w-lg flex flex-col gap-4 p-4 sm:p-5">
				<fieldset className="flex min-w-0 flex-col gap-4 border-0 p-0 m-0">
					<legend className="text-sm font-medium text-(--text-primary)">What is this domain for?</legend>
					<div className="mt-4 flex flex-col gap-3">
						<Checkbox
							checked={email}
							label="Email"
							description="Send and receive mail on this domain. Uses MX, SPF and DKIM."
							onChange={(event) => setEmail(event.target.checked)}
						/>
						<Checkbox
							checked={sites}
							label="Website"
							description="Serve a site on this name. The root uses ALIAS or A, not a normal CNAME."
							onChange={(event) => setSites(event.target.checked)}
						/>
						{sites && (
							<div className="ml-[1.625rem] border-l-2 border-(--border-subtle) pl-4">
								<Checkbox
									checked={includeWww}
									label={`Also connect www.${detail.domainName}`}
									description="Adds a CNAME for www. You can turn this off if you only want the root."
									onChange={(event) => setIncludeWww(event.target.checked)}
								/>
							</div>
						)}
					</div>
					<p className="text-xs text-(--text-muted)">
						Hub and Webmail use their own hostnames, such as hub.{detail.domainName} and webmail.{detail.domainName}.
					</p>
				</fieldset>
				{save.isError && (
					<p className="text-sm text-(--color-critical-700)">
						We could not update the purposes. Check that at least one stays on, and that email accounts are
						removed before turning email off.
					</p>
				)}
				<div className="flex flex-wrap gap-2 border-t border-(--border-subtle) pt-4">
					<Button
						variant="secondary"
						disabled={!dirty || (!email && !sites)}
						pending={save.isPending}
						onClick={() => save.mutate()}
					>
						Save purposes
					</Button>
				</div>
			</Card>
		</Section>
	);
}

function Fact({ label, value, footer }: { label: string; value: string; footer?: ReactNode }) {
	return (
		<Card className="flex flex-col gap-1.5">
			<p className="text-xs font-medium uppercase tracking-wide text-(--text-muted)">{label}</p>
			<p className="text-base font-semibold text-(--text-primary)">{value}</p>
			{footer && <p className="text-sm text-(--color-brand-700)">{footer}</p>}
		</Card>
	);
}

function Row({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex flex-wrap items-baseline justify-between gap-2">
			<span className="text-sm text-(--text-secondary)">{label}</span>
			<span className="text-sm text-(--text-primary)">{value}</span>
		</div>
	);
}

function LabelledCopy({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex flex-col gap-1">
			<p className="text-xs font-medium text-(--text-muted)">{label}</p>
			<CopyField value={value} label={label} />
		</div>
	);
}
